import React, { useState } from 'react';
import styled from 'styled-components';
import { User, Class } from '../types/alarm';
import { authService, alarmService } from '../services/firebaseService';
import { sendEmailVerification } from 'firebase/auth';
import { updatePassword } from 'firebase/auth';
import TermsAgreementModal from './TermsAgreementModal';
import { getAvatarInitialsFromEmail, getUiAvatarPlaceholderUrl } from '../utils/uiAvatarPlaceholder';

interface EmailAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 80px 0 160px 0;
  box-sizing: border-box;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  margin: auto;
`;

const Title = styled.h2`
  color: var(--color-text-primary);
  margin-bottom: 20px;
  font-size: 1.5rem;
  font-weight: bold;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const Label = styled.label`
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
  
  &::placeholder {
    color: #999;
  }
  
  /* 다크모드: WebKit이 입력 글자색을 덮어쓰는 경우까지 흰색으로 고정 */
  :root:not([data-theme='light']) & {
    background: var(--color-category-input-bg);
    color: #ffffff;
    -webkit-text-fill-color: #ffffff;
    caret-color: #ffffff;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #f0f0f0 inset !important;
    box-shadow: 0 0 0 30px #f0f0f0 inset !important;
    -webkit-text-fill-color: #111111 !important;
  }
  :root:not([data-theme='light']) &:-webkit-autofill,
  :root:not([data-theme='light']) &:-webkit-autofill:hover,
  :root:not([data-theme='light']) &:-webkit-autofill:focus,
  :root:not([data-theme='light']) &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #2f2f2f inset !important;
    box-shadow: 0 0 0 30px #2f2f2f inset !important;
    -webkit-text-fill-color: #ffffff !important;
    color: #ffffff !important;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  background: ${props => props.primary ? 'var(--color-accent-strong)' : '#bbb'};
  color: ${props => props.primary ? 'white' : 'white'};
  
  &:hover {
    background: ${props => props.primary ? 'var(--color-accent)' : '#999'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.primary ? 'rgba(var(--color-accent-rgb), 0.3)' : 'rgba(187, 187, 187, 0.6)'};
  }
  
  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button<{ primary?: boolean; isCancel?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  background: ${props => {
    if (props.primary) return 'var(--color-accent-strong)';
    if (props.isCancel) return '#bbb';
    return '#bbb';
  }};
  color: ${props => {
    if (props.primary) return 'white';
    if (props.isCancel) return 'white';
    return 'white';
  }};
  
  &:hover {
    background: ${props => {
      if (props.primary) return 'var(--color-accent)';
      if (props.isCancel) return '#999';
      return '#999';
    }};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => {
      if (props.primary) return 'rgba(var(--color-accent-rgb), 0.3)';
      if (props.isCancel) return 'rgba(187, 187, 187, 0.6)';
      return 'rgba(187, 187, 187, 0.6)';
    }};
  }
  
  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
`;

const VerificationMessage = styled.div`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
`;

const StatusMessage = styled.div`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
`;

const CheckIcon = styled.svg`
  width: 16px;
  height: 16px;
  margin-right: 6px;
  fill: #4a5568;
  vertical-align: middle;
`;

const EmailIcon = styled.svg`
  width: 16px;
  height: 16px;
  margin-right: 6px;
  fill: #4a5568;
  vertical-align: middle;
`;

const AgreementSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  text-align: left;
`;

const AgreementItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: var(--color-panel);
  }
`;

const AgreementCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--color-accent-strong);
  pointer-events: none;
`;

const AgreementText = styled.label`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;
  flex: 1;
`;

const EmailAuthModal: React.FC<EmailAuthModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [classGroup, setClassGroup] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // 이메일 인증 가능 여부 확인 (이메일만)
  const canSendVerification = email && email.includes('@') && !isEmailSent;

  // 회원가입 가능 여부 확인 (모든 필수 정보 + 이메일 인증 완료 + 비밀번호 일치 + 약관 동의)
  const canSignUp = email && password && password.length >= 6 && name && classGroup && isEmailVerified && password === passwordConfirm && agreedTerms && agreedPrivacy;

  const handleSendVerification = async () => {
    setError('');
    setSuccess('');

    if (!email) {
      setError('이메일 주소를 입력해주세요.');
      return;
    }

    if (!email.includes('@')) {
      setError('올바른 이메일 주소를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    
    try {
      // 이메일 인증을 위한 임시 계정 생성 (실제 회원가입이 아닌 인증용)
      const tempPassword = 'temp123456';
      const userCredential = await authService.signUp(email, tempPassword);
      const firebaseUser = userCredential;
      
      // Firebase 이메일 인증 메일 전송
      await sendEmailVerification(firebaseUser);
      
      setIsEmailSent(true);
      setSuccess(`인증 메일이 전송되었습니다. 이메일을 확인해주세요.`);
      
      // 이메일 인증 상태 확인을 위한 인터벌 설정
      const checkEmailVerification = setInterval(async () => {
        try {
          await firebaseUser.reload();
          if (firebaseUser.emailVerified) {
            setIsEmailVerified(true);
            setSuccess('이메일 인증이 완료되었습니다!');
            clearInterval(checkEmailVerification);
          }
        } catch (error) {
          console.error('이메일 인증 확인 중 오류:', error);
        }
      }, 3000); // 3초마다 확인
      
      // 5분 후 인터벌 정리
    setTimeout(() => {
        clearInterval(checkEmailVerification);
      }, 300000);
      
    } catch (error: any) {
      console.error('Firebase 회원가입 에러:', error);
      
      let errorMessage = '회원가입에 실패했습니다.';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = '이미 사용 중인 이메일입니다.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '올바른 이메일 주소를 입력해주세요.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = '네트워크 연결을 확인해주세요.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = '너무 많은 요청이 있었습니다. 잠시 후 다시 시도해주세요.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async () => {
    if (!canSignUp) {
      setError('모든 필수 항목을 입력해주세요.');
      return;
    }

    if (!isEmailVerified) {
      setError('이메일 인증을 완료해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // 이메일 인증이 완료된 경우, 실제 회원가입 진행
      // 1. 기존 임시 계정으로 로그인
      const userCredential = await authService.signIn(email, 'temp123456');
      const firebaseUser = userCredential;
      
      // 2. 비밀번호를 사용자가 입력한 값으로 변경
      await updatePassword(firebaseUser, password);

      // 3. 클래스 이름 15자 제한
      const classNameToSave = classGroup.trim().slice(0, 15);

      // 4. 사용자 프로필 저장 (첫 가입 시 무조건 프리 플랜, 약관 동의 정보 포함)
      await alarmService.saveUserProfile(firebaseUser.uid, {
        name: name,
        class: classNameToSave,
        userType: 'free',
        termsAgreed: agreedTerms && agreedPrivacy,
        termsAgreedAt: agreedTerms && agreedPrivacy ? new Date().toISOString() : undefined
      });
      
      // 5. 회원가입 시 입력한 클래스를 첫 번째 클래스로 생성 (15자 이내)
      const initialClass: Class = {
        id: `class_${Date.now()}`,
        name: classNameToSave,
        memberCount: 0,
        classEntryCode: '0000',
        createdAt: new Date(),
        isActive: true
      };

      // 6. 앱 형식의 사용자 정보 생성 (첫 가입 시 무조건 프리 플랜)
      const appUser: User = {
        id: firebaseUser.uid,
        email: firebaseUser.email || email,
        name: name,
        userType: 'free',
        picture: getUiAvatarPlaceholderUrl(getAvatarInitialsFromEmail(firebaseUser.email || email), { size: 40 }),
        currentClassId: initialClass.id
      };

      // 7. 클래스 정보를 Firestore와 로컬 스토리지에 저장
      const { userDataService } = await import('../services/userDataService');
      const { saveClasses, saveCurrentClassId } = await import('../utils/storage');
      
      // Firestore에 클래스 저장
      await userDataService.saveClass(firebaseUser.uid, initialClass);
      
      // 로컬 스토리지에도 백업
      saveClasses(firebaseUser.uid, [initialClass]);
      saveCurrentClassId(firebaseUser.uid, initialClass.id);
      
      setSuccess('회원가입이 완료되었습니다!');
      setTimeout(() => {
        onSuccess(appUser);
      }, 1000);
    } catch (error: any) {
      console.error('Firebase 로그인/비밀번호 변경 에러:', error);
      let errorMessage = '회원가입에 실패했습니다.';
      if (error.code === 'auth/user-not-found') {
        errorMessage = '등록되지 않은 이메일입니다.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = '비밀번호가 올바르지 않습니다.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '올바른 이메일 주소를 입력해주세요.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = '비밀번호는 6자 이상이어야 합니다.';
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // 취소 시 임시로 생성된 Firebase 계정 삭제 시도
    const cleanupTempAccount = async () => {
      try {
        const userCredential = await authService.signIn(email, 'temp123456');
        const firebaseUser = userCredential;
        await firebaseUser.delete();
      } catch (error) {
        console.log('임시 계정 삭제 실패 (무시됨):', error);
      }
    };
    
    // 이메일 인증이 완료된 경우에만 계정 삭제 시도
    if (isEmailSent && isEmailVerified) {
      cleanupTempAccount();
    }
    
    setEmail('');
    setPassword('');
    setPasswordConfirm('');
    setName('');
    setClassGroup('');
    setIsEmailSent(false);
    setIsEmailVerified(false);
    setAgreedTerms(false);
    setAgreedPrivacy(false);
    setError('');
    setSuccess('');
    onClose();
  };

  const handleTermsAgreementConfirm = (terms: boolean, privacy: boolean) => {
    setAgreedTerms(terms);
    setAgreedPrivacy(privacy);
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onClose} />
      <ModalWrapper>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Title>회원가입</Title>
        
        <Form>
          <InputGroup>
            <Label>이메일 *</Label>
            <Input
              type="email"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button 
              type="button" 
                primary={Boolean(canSendVerification && !isLoading)}
              onClick={handleSendVerification}
                disabled={!canSendVerification || isLoading}
                style={{ marginTop: '8px', width: '100%' }}
              >
                {isLoading ? '처리 중...' : isEmailSent ? '인증 메일 전송됨' : '인증 메일 전송'}
              </Button>
            </InputGroup>
            
            {isEmailSent && (
              <InputGroup>
                <VerificationMessage>
                  <EmailIcon viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </EmailIcon>
                  인증 메일이 전송되었습니다. 이메일을 확인하여 링크를 클릭해주세요.
                </VerificationMessage>
                {isEmailVerified && (
                  <VerificationMessage>
                    <CheckIcon viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </CheckIcon>
                  이메일 인증 완료
                </VerificationMessage>
              )}
            </InputGroup>
          )}
          
          <InputGroup>
            <Label>비밀번호 *</Label>
            <Input
              type="password"
              placeholder="6자 이상 입력"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputGroup>
            <InputGroup>
              <Label>비밀번호 확인 *</Label>
              <Input
                type="password"
                placeholder="비밀번호를 다시 입력하세요"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
              />
              {password && passwordConfirm && password !== passwordConfirm && (
                <ErrorMessage>비밀번호가 일치하지 않습니다.</ErrorMessage>
              )}
            </InputGroup>
          
          <InputGroup>
            <Label>이름 *</Label>
            <Input
              type="text"
              placeholder="홍길동"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </InputGroup>
          
          <InputGroup>
            <Label>기본 클래스 이름 * (15자 이내)</Label>
            <Input
              type="text"
              placeholder="0학년 0반"
              value={classGroup}
              onChange={(e) => setClassGroup(e.target.value.slice(0, 15))}
              maxLength={15}
              required
            />
          </InputGroup>
            
          {/* 약관 동의 섹션 */}
          <AgreementSection>
            <AgreementItem onClick={() => setShowTermsModal(true)}>
              <AgreementCheckbox
                type="checkbox"
                checked={agreedTerms}
                readOnly
              />
              <AgreementText>회원가입 약관 동의 (필수)</AgreementText>
            </AgreementItem>
            <AgreementItem onClick={() => setShowTermsModal(true)}>
              <AgreementCheckbox
                type="checkbox"
                checked={agreedPrivacy}
                readOnly
              />
              <AgreementText>개인정보 수집 동의 (필수)</AgreementText>
            </AgreementItem>
          </AgreementSection>
          
          <ButtonGroup>
            <ActionButton 
              data-variant="modal-secondary"
              type="button" 
              onClick={handleCancel}
              isCancel
            >
              취소
            </ActionButton>
            <ActionButton 
              type="button" 
                primary={Boolean(canSignUp && !isLoading)}
                disabled={!canSignUp || isLoading}
              onClick={handleSignUp}
            >
                {isLoading ? '회원가입 중...' : '회원가입 완료'}
            </ActionButton>
          </ButtonGroup>
        </Form>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <StatusMessage>{success}</StatusMessage>}
      </ModalContent>
      </ModalWrapper>
      
      {/* 약관 동의 모달 */}
      <TermsAgreementModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onConfirm={handleTermsAgreementConfirm}
      />
    </>
  );
};

export default EmailAuthModal; 